'use client'

import { useEffect, useMemo, useState } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { html } from '@codemirror/lang-html'
import { EditorView } from '@codemirror/view'
import { HugeiconsIcon } from '@hugeicons/react'
import { Copy01Icon, Tick02Icon } from '@hugeicons-pro/core-stroke-rounded'
import { useTheme } from 'next-themes'
import { developerCodeMirrorChrome } from '@/lib/developer/codemirror-theme'
import { cn } from '@/lib/utils'

type Props = {
  snippet: string
  language?: string
  className?: string
}

function languageExtension(language: string) {
  switch (language.toLowerCase()) {
    case 'html':
    default:
      return html()
  }
}

export function InstallCodeBlock({ snippet, language = 'HTML', className }: Props) {
  const [copied, setCopied] = useState(false)
  const { resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => setMounted(true), [])

  const isDark = mounted && resolvedTheme === 'dark'
  const displayValue = snippet.trim()

  const extensions = useMemo(
    () => [languageExtension(language), EditorView.lineWrapping, developerCodeMirrorChrome(isDark)],
    [isDark, language],
  )

  const handleCopy = () => {
    if (!displayValue) return
    void navigator.clipboard.writeText(displayValue)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div
      className={cn(
        'relative h-full w-full overflow-hidden bg-surface dark:bg-atrium-inner',
        className,
      )}
    >
      <button
        type="button"
        onClick={handleCopy}
        disabled={!displayValue}
        className="absolute right-3 top-3 z-10 inline-flex size-8 items-center justify-center rounded-lg border-[0.5px] border-line bg-surface text-text-muted transition-colors hover:bg-canvas hover:text-text-strong disabled:opacity-40 dark:border-atrium-border dark:bg-atrium-background dark:hover:bg-atrium-content"
        aria-label={copied ? 'Copied' : 'Copy install snippet'}
      >
        <HugeiconsIcon icon={copied ? Tick02Icon : Copy01Icon} className="size-4" />
      </button>

      <div className="absolute inset-0">
        {mounted ? (
          <CodeMirror
            value={displayValue}
            theme={isDark ? 'dark' : 'light'}
            editable={false}
            readOnly
            basicSetup={{
              lineNumbers: true,
              foldGutter: false,
              highlightActiveLine: false,
              highlightActiveLineGutter: false,
              autocompletion: false,
            }}
            extensions={extensions}
            placeholder="<!-- Install snippet will appear once your container is ready -->"
            className="h-full w-full [&_.cm-editor]:h-full [&_.cm-editor]:bg-transparent [&_.cm-editor]:outline-none [&_.cm-scroller]:h-full [&_.cm-scroller]:overflow-auto"
            height="100%"
            width="100%"
          />
        ) : (
          <pre className="h-full overflow-auto px-4 py-4 font-mono text-[12px] leading-6 text-text-muted">
            {displayValue || '<!-- Install snippet will appear once your container is ready -->'}
          </pre>
        )}
      </div>

      <div className="pointer-events-none absolute bottom-3 right-3 z-10">
        <span className="inline-flex items-center rounded-lg border-[0.5px] border-line bg-surface px-2.5 py-1 font-mono text-[11px] font-medium text-text-muted dark:border-atrium-border dark:bg-atrium-background">
          {language}
        </span>
      </div>
    </div>
  )
}
